/**
 * socketActions - plain (hook-free) module for sending client actions.
 *
 * Components import this directly; it registers no listeners or effects.
 * Connection lifecycle and incoming message handling live in
 * useSocketConnection(), which is mounted exactly once in App.
 */

import type { ClientEvent } from '../types/generated';
import { sendMessage } from './socket';
import { useGameStore } from '../stores/gameStore';

// ============================================================================
// Claim status timers (shared with the message handler)
// ============================================================================

/** Safety net: if the server never answers a claim, unstick the UI */
const CLAIM_SAFETY_TIMEOUT_MS = 5000;

let claimSafetyTimer: number | null = null;
let claimResetTimer: number | null = null;

/** Timing info for dev-mode round-trip logging */
export const claimTiming = { startTime: 0, word: '' };

/** Clear any pending claim status timers */
export function clearClaimTimers(): void {
  if (claimSafetyTimer !== null) {
    window.clearTimeout(claimSafetyTimer);
    claimSafetyTimer = null;
  }
  if (claimResetTimer !== null) {
    window.clearTimeout(claimResetTimer);
    claimResetTimer = null;
  }
}

/** Return claim status to idle after a delay (replaces any pending reset) */
export function scheduleClaimIdle(delayMs: number): void {
  if (claimResetTimer !== null) {
    window.clearTimeout(claimResetTimer);
  }
  claimResetTimer = window.setTimeout(() => {
    claimResetTimer = null;
    useGameStore.getState().setClaimStatus('idle');
  }, delayMs);
}

/** Immediately reset claim status to idle and cancel timers */
export function resetClaimStatus(): void {
  clearClaimTimers();
  const { claimStatus, setClaimStatus } = useGameStore.getState();
  if (claimStatus !== 'idle') {
    setClaimStatus('idle');
  }
}

// ============================================================================
// Explicit dictionary lookups (only these are posted to chat)
// ============================================================================

const pendingExplicitLookups = new Map<string, number>();

/**
 * Returns true (and consumes the entry) if a LOOKUP_RESULT for this word
 * answers a lookup the user explicitly requested (e.g. /lookup in chat).
 */
export function consumeExplicitLookup(word: string): boolean {
  const key = word.toUpperCase();
  const count = pendingExplicitLookups.get(key) ?? 0;
  if (count <= 0) return false;
  if (count === 1) {
    pendingExplicitLookups.delete(key);
  } else {
    pendingExplicitLookups.set(key, count - 1);
  }
  return true;
}

export function clearExplicitLookups(): void {
  pendingExplicitLookups.clear();
}

// ============================================================================
// Sending
// ============================================================================

/** Send a message; on failure show a toast and clear loading states */
function send(event: ClientEvent): boolean {
  const sent = sendMessage(event);
  if (!sent) {
    const store = useGameStore.getState();
    store.addToast({ type: 'error', message: 'Not connected to server' });
    store.setCreatingRoom(false);
    store.setJoiningRoom(false);
    store.setStartingGame(false);
    store.setFlippingTile(false);
  }
  return sent;
}

export const socketActions = {
  createRoom(playerName: string): void {
    useGameStore.getState().setCreatingRoom(true);
    send({ type: 'CREATE_ROOM', name: playerName });
  },

  joinRoom(roomCode: string, playerName: string): void {
    useGameStore.getState().setJoiningRoom(true);
    send({ type: 'JOIN_ROOM', roomCode, name: playerName });
  },

  leaveRoom(): void {
    sendMessage({ type: 'LEAVE_ROOM' });
    clearClaimTimers();
    clearExplicitLookups();
    useGameStore.getState().reset();
  },

  startGame(): void {
    useGameStore.getState().setStartingGame(true);
    send({ type: 'START_GAME' });
  },

  flipTile(tileType: 'vowel' | 'consonant'): void {
    useGameStore.getState().setFlippingTile(true);
    send({ type: 'FLIP_TILE', tileType });
  },

  claimWord(word: string, stolenWordIds?: string[]): void {
    const store = useGameStore.getState();
    claimTiming.startTime = performance.now();
    claimTiming.word = word.toUpperCase();

    clearClaimTimers();
    store.setClaimStatus('claiming');
    store.setWordValidation(null); // Clear stale validation indicator

    if (!send({ type: 'CLAIM_WORD', word, stolenWordIds })) {
      store.setClaimStatus('idle');
      return;
    }

    claimSafetyTimer = window.setTimeout(() => {
      claimSafetyTimer = null;
      if (useGameStore.getState().claimStatus === 'claiming') {
        useGameStore.getState().setClaimStatus('idle');
      }
    }, CLAIM_SAFETY_TIMEOUT_MS);
  },

  sendChat(text: string): void {
    send({ type: 'CHAT', message: text });
  },

  /** Explicit, user-requested lookup - the result is posted to chat */
  lookupWord(word: string): void {
    const key = word.toUpperCase();
    if (send({ type: 'LOOKUP_WORD', word: key })) {
      pendingExplicitLookups.set(key, (pendingExplicitLookups.get(key) ?? 0) + 1);
    }
  },

  /** Silent as-you-type validation - only updates the claim input indicator */
  validateWord(word: string): void {
    sendMessage({ type: 'LOOKUP_WORD', word: word.toUpperCase() });
  },

  rematch(): void {
    send({ type: 'REMATCH' });
  },

  // AI player management (host only)
  addAI(difficulty: number): void {
    send({ type: 'ADD_AI', difficulty });
  },

  removeAI(playerId: string): void {
    send({ type: 'REMOVE_AI', playerId });
  },

  updateAIDifficulty(playerId: string, difficulty: number): void {
    send({ type: 'UPDATE_AI_DIFFICULTY', playerId, difficulty });
  },

  updateGameSettings(settings: { turnTimerMs?: number | null; endTimerMs?: number }): void {
    send({ type: 'UPDATE_GAME_SETTINGS', ...settings });
  },

  /** Request a full state sync (recovers AI players after server restart) */
  requestSync(): void {
    send({ type: 'REQUEST_SYNC' });
  },

  togglePause(): void {
    send({ type: 'TOGGLE_PAUSE' });
  },

  /** End game early (only works when playing vs AI only) */
  endGameEarly(): void {
    send({ type: 'END_GAME_EARLY' });
  },
};

export type SocketActions = typeof socketActions;
